import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 生成随机字符串
export function randomString(length: number) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = length; i > 0; --i)
    result += chars[Math.floor(Math.random() * chars.length)];
  return result;
}

export const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Cookie 操作工具函数
export const setCookie = (
  name: string,
  value: string,
  options: {
    expires?: Date;
    maxAge?: number;
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: "strict" | "lax" | "none";
  } = {}
) => {
  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (options.expires) {
    cookieString += `; expires=${options.expires.toUTCString()}`;
  }

  if (options.maxAge !== undefined) {
    cookieString += `; max-age=${options.maxAge}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.secure) {
    cookieString += `; secure`;
  }

  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }

  document.cookie = cookieString;
};

export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const nameEQ = name + "=";
  const ca = document.cookie.split(";");

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
};

export function debounce<T extends Function>(cb: T, wait = 20) {
  let h: ReturnType<typeof setTimeout> | number = 0;
  const callable = (...args: any) => {
    clearTimeout(h);
    h = setTimeout(() => cb(...args), wait);
  };
  return <T>(<any>callable);
}

export const getBase64 = async (file: File) => {
  const p = new Promise((resolve) => {
    const fReader = new FileReader();

    fReader.onload = () => {
      resolve(fReader.result);
    };

    fReader.onerror = () => {
      resolve(null);
    };

    fReader.readAsDataURL(file);
  });

  return p;
};

// Compress image to xx pixels and return base64 string
export const compressImage = (
  file: File,
  maxSize: number = 24
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate the aspect ratio
      const aspectRatio = img.width / img.height;

      // Calculate new dimensions while maintaining aspect ratio
      let newWidth, newHeight;
      if (img.width > img.height) {
        // Landscape image
        newWidth = maxSize;
        newHeight = Math.round(maxSize / aspectRatio);
      } else {
        // Portrait image
        newHeight = maxSize;
        newWidth = Math.round(maxSize * aspectRatio);
      }

      // Create a canvas with the calculated dimensions
      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      // Draw the image resized to new dimensions
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Get base64 representation
      const base64 = canvas.toDataURL(file.type);
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error("Error loading image"));
    };

    // Load the image from the file
    img.src = URL.createObjectURL(file);
  });
};

export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export const clearStorage = () => {
  const authCookieNames = ["Token", "ID", "tenant_id"];

  authCookieNames.forEach((cookieName) => {
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    // 也尝试清空可能存在的其他路径的同名 cookie
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/admin`;
  });

  sessionStorage.clear();
  localStorage.clear();

  window.location.href = "/login";
};

export const downloadFileUsingBlob = (
  fileName: string,
  blob: Blob | string,
  type: string
) => {
  const fileData = new Blob([blob], { type });
  // @ts-ignore
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // for IE
    // @ts-ignore
    window.navigator.msSaveOrOpenBlob(fileData, fileName);
  } else {
    // for Non-IE (chrome, firefox etc.)
    const a = document.createElement("a");
    document.body.appendChild(a);
    // @ts-ignore
    a.style = "display: none";
    a.href = URL.createObjectURL(fileData);
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(a.href);
    a.remove();
  }
};

export const downloadFileUsingLink = (fileName: string, url: string) => {
  const a = document.createElement("a");
  document.body.appendChild(a);
  // @ts-ignore
  a.style = "display: none";
  a.href = url;
  a.target = "_blank";
  a.download = fileName;
  a.click();
  a.remove();
};

export const convertFieldName = (fieldName: string) => {
  return fieldName.split("_").join(" ");
};

export const convertFieldNameToKey = (fieldName: string) => {
  return fieldName.split(" ").join("_").toLowerCase();
};

export const moneyFormatter = (currency?: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "usd"
  });

export function safeParseUrl(input: string, defaultProtocol = "https:") {
  try {
    // 如果输入没有协议，则加上默认协议
    const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(input);
    const url = hasProtocol
      ? new URL(input)
      : new URL(`${defaultProtocol}//${input}`);
    return url;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return null; // 无效 URL
  }
}

export async function convertHtmlToPdf(
  dom: HTMLElement,
  name: string = "myPdf.pdf"
) {
  // a4 size [595.28,841.89]
  const A4_HEIGHT = 841.89;
  const A4_WIDTH = 595.28;

  const WIDTH_MARGIN = 10;
  const HEIGHT_MARGIN = 10;
  const PAGE_HEIGHT = A4_HEIGHT - 2 * HEIGHT_MARGIN;

  /** create pdf instance */
  const pdf = new jsPDF("p", "pt", "a4"); // orientation, unit, format

  // Store original styles to restore later
  const originalStyles = {
    overflow: dom.style.overflow,
    height: dom.style.height,
    maxHeight: dom.style.maxHeight,
    position: dom.style.position,
    left: dom.style.left,
    top: dom.style.top
  };

  // Temporarily modify styles to ensure all content is visible
  dom.style.overflow = "visible";
  dom.style.height = "auto";
  dom.style.maxHeight = "none";
  dom.style.position = "relative";
  dom.style.left = "0";
  dom.style.top = "0";

  // Force layout recalculation
  dom.offsetHeight;

  try {
    /** convert html to canvas */
    const canvas = await html2canvas(dom, {
      useCORS: true,
      allowTaint: true,
      scale: 2, // Increase scale for better quality (2x resolution)
      scrollX: 0,
      scrollY: 0,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      width: dom.scrollWidth,
      height: dom.scrollHeight,
      x: 0,
      y: 0,
      logging: false,
      backgroundColor: "#ffffff", // Ensure white background
      removeContainer: true,
      imageTimeout: 15000, // Increase timeout for image loading
      onclone: (clonedDoc) => {
        // Ensure all elements in the cloned document are visible
        const clonedElement =
          clonedDoc.getElementById(dom.id) ||
          clonedDoc.querySelector("[data-html2canvas-clone]");
        if (clonedElement) {
          clonedElement.style.overflow = "visible";
          clonedElement.style.height = "auto";
          clonedElement.style.maxHeight = "none";
          clonedElement.style.position = "relative";

          // Make all child elements visible and improve rendering
          const allElements = clonedElement.querySelectorAll("*");
          allElements.forEach((el: Element) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.overflow = "visible";
            htmlEl.style.maxHeight = "none";
            htmlEl.style.height = "auto";
            // Improve text rendering
            (htmlEl.style as any).webkitFontSmoothing = "antialiased";
            (htmlEl.style as any).fontSmooth = "always";
            htmlEl.style.textRendering = "optimizeLegibility";
          });
        }
      }
    });

    /** calculate the imgWidth, imgHeight to print on PDF
     *  so it can scale in equal proportions*/
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const imgWidth = A4_WIDTH - 2 * WIDTH_MARGIN;
    const imgHeight = (imgWidth / canvasWidth) * canvasHeight;

    /** print pageImg on to pdf */
    const pageImg = canvas.toDataURL("image/jpeg", 0.95);

    if (imgHeight > PAGE_HEIGHT) {
      // need multi page pdf
      const totalPages = Math.ceil(imgHeight / PAGE_HEIGHT);

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        if (pageIndex > 0) {
          pdf.addPage();
        }

        // Calculate the source and destination dimensions for this page
        const sourceY = (pageIndex * PAGE_HEIGHT * canvasHeight) / imgHeight;
        const sourceHeight = Math.min(
          (PAGE_HEIGHT * canvasHeight) / imgHeight,
          canvasHeight - sourceY
        );

        // Create a temporary canvas for this page slice
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sourceHeight;
        const pageCtx = pageCanvas.getContext("2d");

        if (pageCtx) {
          // Draw the slice of the original canvas
          pageCtx.drawImage(
            canvas,
            0,
            sourceY,
            canvasWidth,
            sourceHeight,
            0,
            0,
            canvasWidth,
            sourceHeight
          );

          // Convert this page slice to image
          const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.95);
          const pageImgHeight = (imgWidth / canvasWidth) * sourceHeight;

          // Add the page image to PDF
          pdf.addImage(
            pageImgData,
            "JPEG",
            WIDTH_MARGIN,
            HEIGHT_MARGIN,
            imgWidth,
            pageImgHeight
          );
        }
      }
    } else {
      // print single page pdf
      pdf.addImage(
        pageImg, // img DataUrl
        "JPEG",
        WIDTH_MARGIN, // x - position against the left edge of the page
        HEIGHT_MARGIN, // y - position against the upper edge of the page
        imgWidth,
        imgHeight
      );
    }

    /** save the pdf */
    pdf.save(name);
  } finally {
    // Restore original styles
    dom.style.overflow = originalStyles.overflow;
    dom.style.height = originalStyles.height;
    dom.style.maxHeight = originalStyles.maxHeight;
    dom.style.position = originalStyles.position;
    dom.style.left = originalStyles.left;
    dom.style.top = originalStyles.top;
  }
}
export const hexToOklab = (hex: string) => {
  if (hex.startsWith("oklch") || hex.startsWith("oklab")) {
    return hex;
  }

  if (!hex || typeof hex !== "string") {
    return "oklab(0.58 0.23 0.108)";
  }

  hex = hex.replace("#", "");

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (hex.length !== 6) {
    return "oklab(0.58 0.23 0.108)";
  }

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const linearize = (c) => {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const rLinear = linearize(r);
  const gLinear = linearize(g);
  const bLinear = linearize(b);

  const x = 0.4124564 * rLinear + 0.3575761 * gLinear + 0.1804375 * bLinear;
  const y = 0.2126729 * rLinear + 0.7151522 * gLinear + 0.072175 * bLinear;
  const z = 0.0193339 * rLinear + 0.119192 * gLinear + 0.9503041 * bLinear;

  const l = Math.cbrt(0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z);
  const m = Math.cbrt(0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z);
  const s = Math.cbrt(0.0482003018 * x + 0.2643662691 * y + 0.633851707 * z);

  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  return `oklab(${L.toFixed(3)} ${A.toFixed(3)} ${B.toFixed(3)})`;
};

// 生成 oklab 颜色的深色变体
export const getDarkerOklab = (
  oklabStr: string,
  darkenAmount: number = 0.2
): string => {
  // 解析 oklab 字符串
  const match = oklabStr.match(/oklab\(([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\)/);

  if (!match) {
    // 如果不是 oklab 格式，先转换为 oklab
    return getDarkerOklab(hexToOklab(oklabStr), darkenAmount);
  }

  let L = parseFloat(match[1]);
  const A = parseFloat(match[2]);
  const B = parseFloat(match[3]);

  // 当颜色接近黑色时（亮度小于0.3），返回特定的中等灰色
  if (L < 0.3) {
    return "oklab(0.514 0.000 0.000)";
  }

  // 降低亮度 (L 分量)，但保持在有效范围内
  L = Math.max(0, L * (1 - darkenAmount));

  return `oklab(${L.toFixed(3)} ${A.toFixed(3)} ${B.toFixed(3)})`;
};

// 判断 oklab 颜色是暗色还是明亮色
export const isOklabDark = (oklabStr: string): boolean => {
  // 解析 oklab 字符串
  const match = oklabStr.match(/oklab\(([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\)/);

  if (!match) {
    // 如果不是 oklab 格式，先转换为 oklab 再判断
    return isOklabDark(hexToOklab(oklabStr));
  }

  const L = parseFloat(match[1]);

  // 在 oklab 色彩空间中，L 分量表示亮度
  // 0 = 黑色，1 = 白色
  // 通常以 0.5 作为分界点，小于 0.5 为暗色，大于等于 0.5 为明亮色
  return L < 0.5;
};

// 检测是否在 iframe 中运行
export const isInIframe = () => {
  try {
    return window.self !== window.top;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false;
  }
};

// 格式化文件大小的函数
export const coverFileSize = (
  bytes: number
): { number: string; size: string } => {
  if (!bytes || bytes === 0) return { number: "0", size: "B" };

  // 处理负数情况，保留负号并计算绝对值
  const isNegative = bytes < 0;
  const absoluteBytes = Math.abs(bytes);

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(absoluteBytes) / Math.log(k));

  const formattedSize = parseFloat((absoluteBytes / Math.pow(k, i)).toFixed(2));
  return {
    number: (isNegative ? "-" : "") + formattedSize,
    size: sizes[i]
  };
};

export const formatFileSize = (bytes: number, isHtml = false): string => {
  const { number, size } = coverFileSize(bytes);
  if (isHtml) {
    return `<span class="text-base font-semibold">${number}</span><span class="text-xs font-medium">${size}</span>`;
  }
  return number + size;
};


export const generateAudioUrl = (
  externalConversationId: string,
  environment: "dev" | "prod" = "dev"
): string => {
  const bucketName =
    environment === "dev"
      ? "dev-astra-voice-agent-files"
      : "prod-astra-voice-agent-files";

  return `https://storage.googleapis.com/${bucketName}/whatsappcall/conversation_${externalConversationId}_merged.opus`;
};


export const exportAudioFile = (
  externalConversationId: string,
  fileName?: string,
  environment: "dev" | "prod" = "dev"
) => {
  const audioUrl = generateAudioUrl(externalConversationId, environment);
  const defaultFileName = `conversation_${externalConversationId}_audio.opus`;

  downloadFileUsingLink(fileName || defaultFileName, audioUrl);
};
